import React, { useCallback, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

export function SelectionBox3D({
  selectionBox,
  onStartSelection,
  onUpdateSelection,
  onFinishSelection,
  enabled = true,
}) {
  const { camera, gl } = useThree();
  const isDraggingRef = useRef(false);

  // Mausposition zu Weltkoordinaten
  const getWorldPosition = useCallback((event) => {
    const rect = gl.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const vector = new THREE.Vector3(x, y, 0);
    vector.unproject(camera);

    return { x: vector.x, y: vector.y };
  }, [camera, gl]);

  // Pointer-Down auf dem unsichtbaren Hintergrund
  const handlePointerDown = useCallback((e) => {
    if (!enabled) return;

    // Nur linke Maustaste
    if (e.button !== 0) return;

    e.stopPropagation();
    isDraggingRef.current = true;

    const pos = getWorldPosition(e);
    onStartSelection?.(pos.x, pos.y);

    const handlePointerMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const movePos = getWorldPosition(moveEvent);
      onUpdateSelection?.(movePos.x, movePos.y);
    };

    const handlePointerUp = () => {
      isDraggingRef.current = false;
      onFinishSelection?.();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [enabled, getWorldPosition, onStartSelection, onUpdateSelection, onFinishSelection]);

  // Visuelles Rechteck zeichnen
  const boxGeometry = useMemo(() => {
    if (!selectionBox) return null;

    const { start, end, mode } = selectionBox;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    // 4 Ecken des Rechtecks
    return new Float32Array([
      minX, minY, 10,
      maxX, minY, 10,
      maxX, maxY, 10,
      minX, maxY, 10,
      minX, minY, 10, // Schließen
    ]);
  }, [selectionBox]);

  // Farbe basierend auf Modus
  const boxColor = selectionBox?.mode === 'crossing' ? '#22c55e' : '#3b82f6'; // Grün für Crossing, Blau für Window
  const fillColor = selectionBox?.mode === 'crossing' ? '#22c55e' : '#3b82f6';

  return (
    <group>
      {/* Unsichtbare Fläche zum Klicken (nur wenn enabled) */}
      {/* Muss minimale Opacity haben damit Raycasting funktioniert */}
      {enabled && (
        <mesh
          position={[0, 0, -10]}
          onPointerDown={handlePointerDown}
        >
          <planeGeometry args={[500000, 500000]} />
          <meshBasicMaterial
            transparent
            opacity={0.001}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* Selektionsrechteck */}
      {selectionBox && boxGeometry && (
        <>
          {/* Füllung (halbtransparent) */}
          <mesh position={[
            (Math.min(selectionBox.start.x, selectionBox.end.x) + Math.max(selectionBox.start.x, selectionBox.end.x)) / 2,
            (Math.min(selectionBox.start.y, selectionBox.end.y) + Math.max(selectionBox.start.y, selectionBox.end.y)) / 2,
            9
          ]}>
            <planeGeometry args={[
              Math.abs(selectionBox.end.x - selectionBox.start.x),
              Math.abs(selectionBox.end.y - selectionBox.start.y)
            ]} />
            <meshBasicMaterial
              color={fillColor}
              transparent
              opacity={0.1}
              side={THREE.DoubleSide}
            />
          </mesh>

          {/* Rand */}
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={5}
                array={boxGeometry}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color={boxColor}
              linewidth={2}
              linecap="round"
              linejoin="round"
            />
          </line>

          {/* Gestrichelte Linie für Crossing (Rechteck von rechts nach links) */}
          {selectionBox.mode === 'crossing' && (
            <lineDashedMaterial
              color={boxColor}
              dashSize={200}
              gapSize={100}
            />
          )}
        </>
      )}
    </group>
  );
}
